/* eslint-disable import/prefer-default-export */
// v2 response-convention adapters for the Tier-B replacement surface.
//
// When a domain is flipped to the ACH Go backend it adopts the v2 conventions
// ratified in docs/worklog/API-CONTRACT-PARITY.md (decision D2): single-resource
// SHOW responses are wrapped in a `{model:{...}}` envelope (RD-2), where Rails
// returns the bare object. Collection responses already share Rails' top-level
// `{collection,pagination}` shape (RD-3 only changes the pagination META keys,
// which this consumer does not read), so no collection adapter is needed here.
//
// These adapters are host-gated by the caller: a hook resolves its host via
// apiHostFor('<domain>') and passes `isAch` (host === an ACH host, via
// isAchHost). When `isAch` is false the response is returned untouched, so an
// unflipped domain is byte-for-byte unchanged — the adapter is inert on Rails.

// unwrapAchShowModel returns the inner resource from ACH's D2 `{model}` show
// envelope, or the response unchanged for Rails (bare object). The `'model'`
// presence guard keeps it safe if an ACH response ever arrives un-enveloped.
export const unwrapAchShowModel = <T = any>(response: T, isAch: boolean): T => {
  if (isAch && response && typeof response === 'object' && 'model' in (response as object)) {
    return (response as unknown as { model: T }).model;
  }
  return response;
};

// unwrapAchBareCollection returns the inner array from ACH's SPEC §2
// {collection:[...]} envelope for endpoints Rails serves as a BARE JSON array
// (the user role lists: /admin/users/{managers,sales,traffickers} — Rails
// `render json: User.<scope>`, ACH user/map.go MapUserList). No-op on Rails
// (already the bare array) and defensive if an ACH response ever arrives
// un-enveloped.
export const unwrapAchBareCollection = <T = any>(response: unknown, isAch: boolean): T[] => {
  if (
    isAch &&
    response &&
    typeof response === 'object' &&
    Array.isArray((response as { collection?: unknown }).collection)
  ) {
    return (response as { collection: T[] }).collection;
  }
  return response as T[];
};
